import { CommonModule } from '@angular/common';
import {
   Component,
   DestroyRef,
   EventEmitter,
   Input,
   OnChanges,
   Output,
   SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { SelectButtonModule } from 'primeng/selectbutton';

import {
   PmsRiskResolutionAction,
   PmsRiskResolutionChangeResponse
} from '../../../domain/pms-risk-resolution';
import { DatePropertyTimezonePipe } from '../../../pipes/date-property-timezone.pipe';
import {
   HtmlReportRiskResolutionMutationState,
   HtmlReportRiskResolutionStoreService
} from '../../../services/stores/html-report-risk-resolution-store.service';
import { UserStoreService } from '../../../services/stores/user-store.service';
import {
   ReportRiskResolutionState,
   ReportRowRiskResolutionState
} from '../models/report-row-risk-resolution';

export type ReportRowRiskResolutionChangedEvent = {
   rowId: string;
   response: PmsRiskResolutionChangeResponse;
};

@Component({
   selector: 'app-report-row-risk-resolution-modal',
   standalone: true,
   imports: [
      CommonModule,
      FormsModule,
      ButtonModule,
      CheckboxModule,
      DialogModule,
      InputTextareaModule,
      SelectButtonModule,
      DatePropertyTimezonePipe
   ],
   templateUrl: './report-row-risk-resolution-modal.component.html',
   styleUrls: ['./report-row-risk-resolution-modal.component.scss']
})
export class ReportRowRiskResolutionModalComponent implements OnChanges {
   @Input() visible = false;
   @Input() taskInstanceId: number | null = null;
   @Input() pmsDataId: number | null = null;
   @Input() rowId: string | null = null;
   @Input() riskResolution: ReportRowRiskResolutionState | null = null;

   @Output() visibleChange = new EventEmitter<boolean>();
   @Output() resolutionChanged =
      new EventEmitter<ReportRowRiskResolutionChangedEvent>();

   selectedAction: PmsRiskResolutionAction = 'RESOLVE';
   selectedRiskCodes: string[] = [];
   commentDraft = '';

   mutationState: HtmlReportRiskResolutionMutationState = {
      status: 'IDLE',
      errorStatus: null,
      errorMessage: null,
      response: null
   };

   private userDisplayNameById = new Map<number, string>();

   constructor(
      private htmlReportRiskResolutionStore: HtmlReportRiskResolutionStoreService,
      private userStore: UserStoreService,
      private destroyRef: DestroyRef
   ) {
      this.htmlReportRiskResolutionStore.mutationState$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((state) => {
            this.mutationState = state;

            if (
               state.status === 'SUCCESS' &&
               state.response &&
               this.visible &&
               this.rowId
            ) {
               this.resolutionChanged.emit({
                  rowId: this.rowId,
                  response: state.response
               });

               this.closeAfterSuccess();
            }
         });

      this.userStore.users
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((users) => {
            this.userDisplayNameById = new Map(
               users.map((user) => [
                  user.id,
                  user.fullName?.trim() ||
                     `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                     user.username?.trim() ||
                     user.email?.trim() ||
                     `User #${user.id}`
               ])
            );
         });
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (!this.visible) {
         return;
      }

      const contextChanged =
         changes['visible']?.currentValue === true ||
         !!changes['taskInstanceId'] ||
         !!changes['pmsDataId'] ||
         !!changes['rowId'];

      if (contextChanged) {
         this.initializeDialog();
         return;
      }

      if (changes['riskResolution']) {
         this.reconcileSelectionWithCurrentState();
      }
   }

   get actionOptions(): {
      label: string;
      value: PmsRiskResolutionAction;
   }[] {
      const options: {
         label: string;
         value: PmsRiskResolutionAction;
      }[] = [];

      if ((this.riskResolution?.unresolvedRiskCount ?? 0) > 0) {
         options.push({
            label: 'Resolve',
            value: 'RESOLVE'
         });
      }

      if ((this.riskResolution?.resolvedRiskCount ?? 0) > 0) {
         options.push({
            label: 'Unresolve',
            value: 'UNRESOLVE'
         });
      }

      return options;
   }

   get isSaving(): boolean {
      return this.mutationState.status === 'SAVING';
   }

   get canSubmit(): boolean {
      if (
         this.isSaving ||
         !this.taskInstanceId ||
         !this.pmsDataId ||
         !this.rowId?.trim() ||
         !this.riskResolution ||
         !this.selectedRiskCodes.length
      ) {
         return false;
      }

      return this.selectedRiskCodes.every((riskCode) => {
         const risk = this.riskResolution?.risks.find(
            (item) => item.riskCode === riskCode
         );

         return !!risk && this.isRiskActionable(risk);
      });
   }

   get submitLabel(): string {
      if (this.isSaving) {
         return this.selectedAction === 'RESOLVE' ? 'Resolving...' : 'Unresolving...';
      }

      return this.selectedAction === 'RESOLVE'
         ? 'Resolve Selected'
         : 'Unresolve Selected';
   }

   get commentLabel(): string {
      return this.selectedAction === 'RESOLVE'
         ? 'Resolution comment (optional)'
         : 'Unresolve comment (optional)';
   }

   get mutationError(): string {
      if (this.mutationState.status !== 'ERROR') {
         return '';
      }

      if (this.mutationState.errorMessage) {
         return this.mutationState.errorMessage;
      }

      if (this.mutationState.errorStatus === 409) {
         return 'The resolution state changed while you were working. The latest state has been reloaded. Please review it and try again.';
      }

      return 'Unable to update the risk resolution. Your selections and comment have been kept.';
   }

   onDialogVisibleChange(nextVisible: boolean): void {
      if (!nextVisible) {
         this.closeModal();
      }
   }

   onActionChanged(): void {
      this.selectAllActionableRisks();
      this.htmlReportRiskResolutionStore.resetMutation();
   }

   isRiskActionable(risk: ReportRiskResolutionState): boolean {
      return this.selectedAction === 'RESOLVE' ? !risk.isResolved : risk.isResolved;
   }

   getRiskLabel(riskCode: string): string {
      return riskCode
         .trim()
         .toLowerCase()
         .split('_')
         .filter(Boolean)
         .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
         .join(' ');
   }

   getResolvedByLabel(risk: ReportRiskResolutionState): string {
      const resolvedBy = risk.resolution?.resolvedBy;

      if (resolvedBy == null) {
         return '';
      }

      return this.userDisplayNameById.get(resolvedBy) ?? `User #${resolvedBy}`;
   }

   submit(): void {
      if (!this.canSubmit || !this.pmsDataId || !this.rowId) {
         return;
      }

      this.htmlReportRiskResolutionStore.change({
         pmsDataId: this.pmsDataId,
         rowId: this.rowId,
         riskCodes: [...this.selectedRiskCodes],
         action: this.selectedAction,
         comment: this.commentDraft.trim() || null
      });
   }

   closeModal(): void {
      if (this.isSaving) {
         return;
      }

      this.clearDialogState();
      this.htmlReportRiskResolutionStore.resetMutation();
      this.visibleChange.emit(false);
   }

   trackRiskByCode(_: number, risk: ReportRiskResolutionState): string {
      return risk.riskCode;
   }

   private initializeDialog(): void {
      this.htmlReportRiskResolutionStore.resetMutation();
      this.commentDraft = '';

      const hasUnresolved = (this.riskResolution?.unresolvedRiskCount ?? 0) > 0;

      this.selectedAction = hasUnresolved ? 'RESOLVE' : 'UNRESOLVE';

      this.selectAllActionableRisks();
   }

   private selectAllActionableRisks(): void {
      this.selectedRiskCodes =
         this.riskResolution?.risks
            .filter((risk) => this.isRiskActionable(risk))
            .map((risk) => risk.riskCode) ?? [];
   }

   private reconcileSelectionWithCurrentState(): void {
      const actionOptions = this.actionOptions;

      if (!actionOptions.length) {
         this.selectedRiskCodes = [];
         return;
      }

      if (!actionOptions.some((option) => option.value === this.selectedAction)) {
         this.selectedAction = actionOptions[0].value;
      }

      const actionableRiskCodes = new Set(
         this.riskResolution?.risks
            .filter((risk) => this.isRiskActionable(risk))
            .map((risk) => risk.riskCode) ?? []
      );

      this.selectedRiskCodes = this.selectedRiskCodes.filter((riskCode) =>
         actionableRiskCodes.has(riskCode)
      );

      if (!this.selectedRiskCodes.length) {
         this.selectedRiskCodes = Array.from(actionableRiskCodes);
      }
   }

   private closeAfterSuccess(): void {
      this.clearDialogState();
      this.visibleChange.emit(false);
      this.htmlReportRiskResolutionStore.resetMutation();
   }

   private clearDialogState(): void {
      this.selectedRiskCodes = [];
      this.commentDraft = '';
   }
}
